/**
 * Lane C3 (skeleton): the sword arts' sample-style effects from the swordKit primitives only (no shaders, no new
 * pen code). One generator, `makeArt`, turns an art's palette + motif + a skill -> family table into `ArtFx` ports; each
 * art's own file (arts/<art>.ts) holds only that table. Families: slash, thrust (dash, split-body afterimages), qi
 * (ranged: wind passing through, no beam), sweep (ground ring, afterimages), ground (a marked-out area, shock rings),
 * lock (three-blow flurry, afterimages), break (amber tell), power (set piece), buff, heal. Skeleton quality: polish later.
 */
import { add, diamond, hash, isoRing, lerp, line, px, unit, type G, type V } from '../kit';
import type { Scene } from '../skills';
import {
  bladeArc,
  C,
  clamp01,
  fade,
  hitStar,
  isoDisc,
  level,
  puffs,
  ringOn,
  since,
  strikeAt,
  tilesPx,
  windPass,
  type ArtFx,
} from './swordKit';

/** What an art's blows scatter: its motif. */
export type Motif = 'flake' | 'yinyang' | 'moon' | 'chips' | 'rays' | 'ink' | 'wings' | 'ember' | 'drops';
export interface Pal {
  /** Main ramp (blade band, wind) and second ramp (ground, scatter). */
  a: string;
  b: string;
  motif: Motif;
}
export type Family = 'slash' | 'thrust' | 'qi' | 'sweep' | 'ground' | 'lock' | 'break' | 'power' | 'buff' | 'heal';
/** [skill id, family, reach (tiles; ground radius or dash length), Thai name of the move for the page note]. */
export type Row = readonly [id: string, family: Family, reach?: number];

/** The art's motif scattered from `c`, `e` frames after the blow. */
export function motif(g: G, c: V, e: number, P: Pal, seed = 0, n = 7, spread = 1, life = 9): void {
  if (e < 0 || e >= life) return;
  const m = P.motif;
  for (let i = 0; i < n; i++) {
    const ang = hash(i, seed) * Math.PI * 2;
    const sp = (0.5 + hash(i, seed + 1)) * 2.6 * spread;
    const grav = m === 'flake' ? 0.05 : m === 'ember' ? -0.12 : m === 'rays' || m === 'moon' || m === 'wings' || m === 'yinyang' ? 0 : 0.12;
    const x = Math.round(c[0] + Math.cos(ang) * sp * e);
    const y = Math.round(c[1] + Math.sin(ang) * sp * e * 0.65 + grav * e * e);
    level(g, fade(e, life * 0.5, life * 0.8));
    const hi = C('white', 8);
    const A = (k: number): string => C(P.a, k);
    const B = (k: number): string => C(P.b, k);
    switch (m) {
      case 'flake':
        px(g, x, y, hi);
        px(g, x - 1, y, A(5));
        px(g, x + 1, y, A(5));
        px(g, x, y - 1, A(5));
        px(g, x, y + 1, A(5));
        break;
      case 'yinyang': {
        const o = i % 2 ? 1 : -1;
        px(g, x, y, i % 2 ? hi : C('ink', 1));
        px(g, x + o, y, i % 2 ? A(5) : C('ink', 3));
        px(g, x, y + 1, i % 2 ? A(4) : C('ink', 2));
        break;
      }
      case 'moon':
        px(g, x, y - 1, hi);
        px(g, x - 1, y, A(5));
        px(g, x - 1, y + 1, A(4));
        px(g, x, y + 2, B(4));
        break;
      case 'chips':
        px(g, x, y, A(5));
        px(g, x + 1, y, A(4));
        px(g, x, y + 1, B(3));
        px(g, x + 1, y + 1, B(2));
        break;
      case 'rays': {
        const L = 3 + (i % 3);
        line(g, [x, y], [x + Math.cos(ang) * L, y + Math.sin(ang) * L * 0.65], i % 2 ? hi : A(5));
        break;
      }
      case 'ink':
        px(g, x, y, B(1));
        px(g, x + 1, y, B(2));
        px(g, x, y + 1, A(2));
        if (i % 3 === 0) px(g, x - 1, y - 1, hi);
        break;
      case 'wings':
        diamond(g, x - 2, y, 1, A(4), hi);
        diamond(g, x + 2, y, 1, A(4), hi);
        break;
      case 'ember':
        px(g, x, y, i % 2 ? C('fire', 5) : A(5));
        px(g, x, y + 1, A(3));
        break;
      case 'drops':
        px(g, x, y, A(4));
        px(g, x, y + 1, A(3));
        if (i % 3 === 0) px(g, x + 1, y, B(5));
        break;
    }
  }
  g.globalAlpha = 1;
}

const move100 = (len: number, start: number) => (s: Scene, t: number): V => {
  const k0 = clamp01((t - start) / 0.24);
  const k = 1 - (1 - k0) * (1 - k0);
  const back = 1 - clamp01((t - (start + 1.5)) / 0.3);
  return [s.d[0] * len * k * back, s.d[1] * len * k * back];
};

/** Glint at the blade tip held before a blow. */
function glint(g: G, s: Scene, t0: number, t1: number, P: Pal): void {
  if (s.t < t0 || s.t >= t1) return;
  const { tip } = s.limbs(s.t);
  level(g, 3);
  diamond(g, tip[0], tip[1], s.t < (t0 + t1) / 2 ? 1 : 2, C(P.a, 5), C('white', 8));
  g.globalAlpha = 1;
}

/** The blow's landing: star + motif + a short wind passing out past the foe. */
function landing(g: G, s: Scene, at: number, P: Pal, seed: number, big = 1, wind = true): void {
  const e = since(s.t, at);
  const c = strikeAt(s);
  hitStar(g, s, c, e, P.a, big);
  motif(g, c, e - 1, P, seed, 6 + Math.round(big * 3), big);
  if (wind && e >= 0) {
    const from = add(c, s.d, -4);
    windPass(g, from, [s.d[0], s.d[1]], e, { n: 5, speed: 10, len: 14, spread: 12, life: 8, ramp: P.a, seed });
  }
}

/** A ground ring of `rx` marked out, then shock rings and motif specks flung from its edge. */
function groundBurst(g: G, s: Scene, hit: number, rx: number, P: Pal, seed: number): void {
  const c = s.a;
  const tell = since(s.t, hit - 0.4);
  if (s.t < hit) {
    level(g, 3);
    ringOn(g, c, rx, -Math.PI / 2, clamp01(tell / 4), C(P.a, 4), true);
    g.globalAlpha = 1;
    return;
  }
  const e = since(s.t, hit);
  if (e > 12) return;
  if (e < 3) isoDisc(g, c, rx * (0.45 + e * 0.2), C(P.b, 3), C(P.a, 5), e < 1 ? 2 : 1);
  for (let k = 0; k < 2; k++) {
    const ee = e - k * 2;
    if (ee < 0 || ee > 8) continue;
    level(g, fade(ee, 3, 6));
    isoRing(g, c[0], c[1], Math.round(rx * (0.5 + 0.5 * clamp01(ee / 5))), k ? C(P.b, 5) : C(P.a, 6), k === 0);
  }
  g.globalAlpha = 1;
  const N = 9;
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2 + hash(i, seed) * 0.3;
    motif(g, [c[0] + Math.cos(a) * rx * 0.85, c[1] + Math.sin(a) * rx * 0.85 * 0.5], e - 1, P, seed + i, 2, 0.7, 8);
  }
}

const NOTE: Record<Family, string> = {
  slash: 'ประกายปลายกระบี่ -> แถบฟันตามทางคม -> ดาวกระแทกและลายประจำวิชา -> จาง',
  thrust: 'ย่อตัว ฝุ่นยก -> พุ่งทะลุเป้า เงาร่างแยกตามหลัง -> แถบคมและลายประจำวิชา -> จาง',
  qi: 'ลมรวมปลายกระบี่ -> ลมพัดผ่านเป้าออกไปด้านหลัง (ไม่มีลำแสง) -> ดาวและลายวิชา -> จาง',
  sweep: 'วงพื้นกำหนดเขต -> กวาดรอบตัว เงาร่างแยก -> คลื่นพื้นขยาย ลายวิชากระจายที่ขอบวง',
  ground: 'วงพื้นวาดรอบตัว -> แรงกระแทกลงพื้น -> คลื่นพื้นสองระลอก ลายวิชากระจายที่ขอบวง',
  lock: 'ฟันสามระลอกต่อเนื่อง เงาร่างแยกระหว่างจังหวะ -> ดาวทุกครั้งที่โดน -> จาง',
  break: 'วงอำพันที่เท้า (สัญญาณท่าทำลาย) -> แทงทะลวง เงาร่างแยก -> ดาวและลายวิชา',
  power: 'รวมพลังเป็นวงที่เท้า -> ฟันหนักเงาร่างแยกสามตัว -> คลื่นพื้นและลายวิชากระจายกว้าง',
  buff: 'วงแหวนลอยขึ้นรอบตัวสามชั้น -> ลายวิชาลอยตามขึ้นไป -> จางหาย',
  heal: 'ประกายลอยขึ้นจากพื้น วงแหวนอ่อนรอบตัว -> จางหาย',
};

/** One skill's port. */
function one(art: string, P: Pal, [skill, fam, reach]: Row, idx: number): ArtFx {
  const id = `${art}-${skill}`;
  const seed = idx * 7 + 3;
  const base = { id, skill, th: skill, on: true, weapon: 'sword' as const, note: NOTE[fam] };
  switch (fam) {
    case 'slash':
      return {
        ...base,
        acts: [{ m: 'wxCleave', t0: 0.2, t1: 1.0 }],
        hits: [0.6],
        pre: 0.4,
        hit: 0.6,
        end: 1.5,
        afterimage: null,
        over(g, s) {
          glint(g, s, 0.25, 0.45, P);
          bladeArc(g, s, 0.4, 0.75, P.a, 3);
          landing(g, s, 0.6, P, seed, 1, false);
        },
      };
    case 'qi':
      return {
        ...base,
        acts: [{ m: 'wxQi', t0: 0.3, t1: 1.3 }],
        hits: [0.8],
        big: 0.8,
        pre: 0.5,
        hit: 0.8,
        end: 1.75,
        afterimage: null,
        over(g, s) {
          if (s.t >= 0.3 && s.t < 0.62) {
            const { tip } = s.limbs(s.t);
            const w = since(s.t, 0.3);
            for (let i = 0; i < 3; i++) {
              level(g, fade(w, 3, 4));
              const a = i * 2.1 + 0.4;
              const from: V = [tip[0] - Math.cos(a) * (12 - w * 2), tip[1] - Math.sin(a) * (7 - w)];
              line(g, from, lerp(from, tip, 0.5), i ? C(P.a, 5) : C('white', 7));
            }
            g.globalAlpha = 1;
          }
          glint(g, s, 0.4, 0.7, P);
          windPass(g, s.limbs(0.74).tip, unit([strikeAt(s)[0] - s.limbs(0.74).tip[0], strikeAt(s)[1] - s.limbs(0.74).tip[1]]), since(s.t, 0.74), {
            n: 9,
            speed: 15,
            len: 20,
            spread: 14,
            life: 10,
            ramp: P.a,
            seed,
          });
          landing(g, s, 0.8, P, seed, 1.2, false);
        },
      };
    case 'thrust': {
      const D = reach ?? 100;
      const hit = 0.63;
      return {
        ...base,
        acts: [{ m: 'wxThrust', t0: 0.3, t1: 1.2 }],
        hits: [hit],
        big: hit,
        ghosts: [0.53, 0.57, 0.61],
        pre: 0.45,
        hit,
        end: 1.8,
        dash: D,
        afterimage: 'dash',
        move: move100(D, 0.5),
        under(g, s) {
          puffs(g, s.a, since(s.t, 0.48), 6, 3, 2, 'brown', 6, 0.3, seed);
        },
        over(g, s) {
          glint(g, s, 0.35, 0.5, P);
          bladeArc(g, s, 0.5, 0.85, P.a, 3);
          landing(g, s, hit, P, seed);
        },
      };
    }
    case 'break':
      return {
        ...base,
        acts: [{ m: 'wxThrust', t0: 0.2, t1: 1.0 }],
        hits: [0.65],
        big: 0.65,
        ghosts: [0.5, 0.57],
        stepIn: 6,
        pre: 0.4,
        hit: 0.65,
        end: 1.6,
        afterimage: 'port',
        under(g, s) {
          const t = since(s.t, 0.2);
          if (t >= 0 && s.t < 0.7) {
            level(g, 3);
            ringOn(g, s.a, 14, -Math.PI / 2, clamp01(t / 5), C('fire', 4), true);
            g.globalAlpha = 1;
          }
        },
        over(g, s) {
          glint(g, s, 0.3, 0.5, P);
          bladeArc(g, s, 0.45, 0.8, P.a, 4);
          landing(g, s, 0.65, P, seed, 1.3);
        },
      };
    case 'lock':
      return {
        ...base,
        acts: [
          { m: 'wxCleave', t0: 0.2, t1: 0.55 },
          { m: 'wxCleave', t0: 0.6, t1: 0.95 },
          { m: 'wxThrust', t0: 1.0, t1: 1.3 },
        ],
        hits: [0.45, 0.8, 1.15],
        big: 1.15,
        ghosts: [0.4, 0.7, 1.05],
        stepIn: 8,
        pre: 0.3,
        hit: 0.45,
        end: 2.0,
        afterimage: 'port',
        over(g, s) {
          bladeArc(g, s, 0.3, 0.55, P.a, 3);
          bladeArc(g, s, 0.65, 0.9, P.b, 3);
          bladeArc(g, s, 1.0, 1.25, P.a, 4);
          landing(g, s, 0.45, P, seed, 0.9, false);
          landing(g, s, 0.8, P, seed + 1, 1, false);
          landing(g, s, 1.15, P, seed + 2, 1.4);
        },
      };
    case 'sweep':
    case 'ground': {
      const rx = Math.min(112, tilesPx(reach ?? 4));
      const hit = fam === 'sweep' ? 0.7 : 0.75;
      return {
        ...base,
        acts:
          fam === 'sweep'
            ? [
                { m: 'wxCleave', t0: 0.3, t1: 0.8 },
                { m: 'wxRise', t0: 0.8, t1: 0.9 },
                { m: 'wxCleave', t0: 0.9, t1: 1.4 },
              ]
            : [
                { m: 'wxPower', t0: 0.1, t1: 0.7 },
                { m: 'wxCleave', t0: 0.75, t1: 1.1 },
              ],
        hits: fam === 'sweep' ? [hit, 1.2] : [hit],
        big: hit,
        ghosts: fam === 'sweep' ? [0.6, 0.68, 0.76] : [0.7, 0.78],
        stepIn: fam === 'sweep' ? 6 : 0,
        pre: 0.45,
        hit,
        end: 1.9,
        afterimage: 'port',
        under(g, s) {
          groundBurst(g, s, hit, rx, P, seed);
        },
        over(g, s) {
          glint(g, s, 0.3, 0.5, P);
          bladeArc(g, s, 0.55, 1.1, P.a, 3, 9);
          landing(g, s, hit, P, seed, 1.1, false);
        },
      };
    }
    case 'power': {
      const rx = Math.min(100, tilesPx(reach ?? 3.5));
      return {
        ...base,
        acts: [
          { m: 'wxPower', t0: 0.05, t1: 1.0 },
          { m: 'wxThrust', t0: 1.1, t1: 1.4 },
        ],
        hits: [1.2],
        big: 1.2,
        ghosts: [1.0, 1.08, 1.16],
        stepIn: 0,
        cast: [0.1, 1.0],
        pre: 0.9,
        hit: 1.2,
        end: 2.4,
        afterimage: 'port',
        under(g, s) {
          groundBurst(g, s, 1.2, rx, P, seed);
          const w = since(s.t, 0.1);
          if (w >= 0 && s.t < 1.1) puffs(g, s.a, w % 8, 4, 12, -1.2, P.b, 8, 0, seed);
        },
        over(g, s) {
          glint(g, s, 0.7, 1.1, P);
          bladeArc(g, s, 1.05, 1.4, P.a, 5);
          landing(g, s, 1.2, P, seed, 1.6);
        },
      };
    }
    case 'buff':
    case 'heal': {
      const heal = fam === 'heal';
      return {
        ...base,
        acts: [{ m: heal ? 'wxHeal' : 'wxBuff', t0: 0.1, t1: 2.0 }],
        hits: [],
        pre: 0.1,
        hit: 0.9,
        end: 2.4,
        afterimage: null,
        under(g, s) {
          const e = since(s.t, 0.1);
          if (e < 0 || e > 26) return;
          level(g, fade(e, 14, 20));
          isoRing(g, s.a[0], s.a[1], 10 + Math.min(e, 8), C(P.b, 5), true);
          g.globalAlpha = 1;
        },
        over(g, s) {
          const e = since(s.t, 0.1);
          if (e < 0 || e > 26) return;
          const h = s.chest * 1.6;
          for (let i = 0; i < 3; i++) {
            const k = (e - i * 4) / 12;
            if (k < 0 || k > 1) continue;
            level(g, k < 0.7 ? 3 : 2);
            isoRing(g, s.a[0], s.a[1] - k * h, 9, i % 2 ? C(P.b, 5) : C(P.a, 6), i === 0);
          }
          g.globalAlpha = 1;
          if (e < 22) for (let i = 0; i < 5; i++) {
            const x = s.a[0] + (hash(i, seed) - 0.5) * 18;
            const y = s.a[1] - ((e * 2 + i * 9) % (h + 6));
            level(g, 3);
            px(g, x, y, i % 2 ? C('white', 8) : C(P.a, 5));
          }
          g.globalAlpha = 1;
          if (!heal) motif(g, [s.a[0], s.a[1] - s.chest], e - 2, P, seed, 5, 0.6, 14);
        },
      };
    }
  }
}

/** An art's effects: its skill -> family rows in its palette and motif. */
export function makeArt(art: string, P: Pal, rows: readonly Row[]): readonly ArtFx[] {
  return rows.map((r, i) => one(art, P, r, i));
}
